import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Button, Typography, Alert, Paper, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BuildIcon from "@mui/icons-material/Build";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// ============================================================
// TYPES
// ============================================================
interface WordOption {
  word: string;
  index: number;
  setting: number;
}

interface SentenceBuildingQuestion {
  id: string;
  type: "4";
  question: string;
  points: number;
  module: string;
  skill: string;
  week: number;
  program: string;
  wordOptions: WordOption[];
}

interface SentenceBuildingComponentProps {
  question: SentenceBuildingQuestion;
  userAnswer?: string[] | string;
  onAnswerChange: (answer: string[]) => void;
  onComplete?: (isCorrect: boolean, timeSpent: number) => void;
  disabled?: boolean;
  reviewMode?: boolean;
  showResults?: boolean;
}

interface DraggableWord extends WordOption {
  fromArranged?: boolean;
}

// ============================================================
// STYLED COMPONENTS
// ============================================================
const QuestionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: "1000px",
  margin: "0 auto",
}));

const QuestionHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
}));

const InstructionsContainer = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.info.light + "15",
  border: `1px solid ${theme.palette.info.light}`,
}));

const WorkspaceContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  border: `2px dashed ${theme.palette.primary.light}`,
  borderRadius: theme.spacing(2),
  minHeight: "120px",
  position: "relative",
}));

const WordBankContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
}));

const WordChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(1),
  fontSize: "1rem",
  fontWeight: 500,
  cursor: "grab",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
  },
  "&:active": {
    cursor: "grabbing",
    transform: "scale(0.95)",
  },
}));

const DroppableArea = styled(Box)(({ theme }) => ({
  minHeight: "80px",
  border: `2px dashed ${theme.palette.primary.light}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "transparent",
  transition: "all 0.3s ease",
  "&.drag-over": {
    backgroundColor: theme.palette.primary.light + "20",
    borderColor: theme.palette.primary.main,
    borderStyle: "solid",
  },
}));

const PlacedWordChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontSize: "1rem",
  fontWeight: 500,
  backgroundColor: theme.palette.primary.light + "30",
  border: `1px solid ${theme.palette.primary.light}`,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.light + "50",
    transform: "translateY(-1px)",
  },
}));

const ResultContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  textAlign: "center",
  border: `2px solid`,
  borderRadius: theme.spacing(2),
}));

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const getCorrectSentence = (wordOptions: WordOption[]): string => {
  return [...wordOptions]
    .sort((a, b) => a.index - b.index)
    .map((w) => w.word)
    .join(" ");
};

const parseUserAnswer = (
  userAnswer: string[] | string | undefined
): string[] => {
  if (!userAnswer) return [];

  if (Array.isArray(userAnswer)) {
    return userAnswer;
  }

  if (typeof userAnswer === "string") {
    try {
      const parsed = JSON.parse(userAnswer);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Not JSON
    }
  }

  return [];
};

const reconstructArrangedWords = (
  answerArray: string[],
  wordOptions: WordOption[]
): WordOption[] => {
  const result: WordOption[] = [];

  for (const word of answerArray) {
    const found = wordOptions.find((wo) => wo.word === word);
    if (found) {
      result.push(found);
    }
  }

  return result;
};

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const SentenceBuildingComponent: React.FC<SentenceBuildingComponentProps> = ({
  question,
  userAnswer,
  onAnswerChange,
  onComplete,
  disabled = false,
  reviewMode = false,
  showResults = false,
}) => {
  const theme = useTheme<Theme>();
  const startTimeRef = useRef(Date.now());

  // Track which question we've initialized for
  const initializedQuestionIdRef = useRef<string | null>(null);

  // State management
  const [availableWords, setAvailableWords] = useState<WordOption[]>([]);
  const [arrangedWords, setArrangedWords] = useState<WordOption[]>([]);
  const [draggedWord, setDraggedWord] = useState<DraggableWord | null>(null);

  // Memoized correct answer
  const correctSentence = useMemo(() => {
    if (!question?.wordOptions) return "";
    return getCorrectSentence(question.wordOptions);
  }, [question]);

  // Check if current arrangement is correct
  const isCorrect = useMemo(() => {
    if (
      !question?.wordOptions ||
      arrangedWords.length !== question.wordOptions.length
    ) {
      return false;
    }
    const userSentence = arrangedWords.map((w) => w.word).join(" ");
    return userSentence === correctSentence;
  }, [arrangedWords, correctSentence, question]);

  // Check if all words are placed
  const isComplete = useMemo(() => {
    if (!question?.wordOptions) return false;
    return arrangedWords.length === question.wordOptions.length;
  }, [arrangedWords, question]);

  // ============================================================
  // SYNC ANSWER TO PARENT
  // ============================================================
  const syncAnswerToParent = useCallback(
    (newArrangedWords: WordOption[]) => {
      const answerArray = newArrangedWords.map((w) => w.word);
      console.log("[SentenceBuilding] Syncing to parent:", answerArray);
      onAnswerChange(answerArray);
    },
    [onAnswerChange]
  );

  // ============================================================
  // INITIALIZATION - Reset when question.id changes
  // ============================================================
  useEffect(() => {
    if (!question?.wordOptions || question.wordOptions.length === 0) return;

    // Only re-initialize if question changed
    if (initializedQuestionIdRef.current === question.id) {
      return;
    }

    console.log("[SentenceBuilding] Initializing for question:", question.id);
    initializedQuestionIdRef.current = question.id;
    startTimeRef.current = Date.now();

    const parsedAnswer = parseUserAnswer(userAnswer);
    console.log("[SentenceBuilding] Parsed userAnswer:", parsedAnswer);

    if (parsedAnswer.length > 0) {
      // Restore from saved answer
      const arranged = reconstructArrangedWords(
        parsedAnswer,
        question.wordOptions
      );

      if (arranged.length > 0) {
        console.log("[SentenceBuilding] Restoring arranged words:", arranged);
        setArrangedWords(arranged);

        // Set remaining words as available
        const usedWords = new Set(arranged.map((w) => w.word));
        const remaining = question.wordOptions.filter(
          (wo) => !usedWords.has(wo.word)
        );
        setAvailableWords(shuffleArray(remaining));
        return;
      }
    }

    // No valid saved answer - shuffle all words for word bank
    console.log("[SentenceBuilding] Fresh start - shuffling words");
    setAvailableWords(shuffleArray([...question.wordOptions]));
    setArrangedWords([]);
  }, [question?.id, question?.wordOptions, userAnswer]);

  // ============================================================
  // VALIDATION
  // ============================================================
  if (!question || !question.wordOptions || question.wordOptions.length === 0) {
    return (
      <Alert severity="error">
        Invalid question data: No word options provided
      </Alert>
    );
  }

  const hasValidWordData = question.wordOptions.every(
    (word) =>
      word.word &&
      typeof word.index === "number" &&
      typeof word.setting === "number"
  );

  if (!hasValidWordData) {
    return (
      <Alert severity="error">
        Invalid question data: Word options must have word, index, and setting
      </Alert>
    );
  }

  // ============================================================
  // DRAG AND DROP HANDLERS
  // ============================================================
  const handleDragStart =
    (word: WordOption, isFromArranged: boolean) => (event: React.DragEvent) => {
      if (disabled || reviewMode) {
        event.preventDefault();
        return;
      }
      setDraggedWord({ ...word, fromArranged: isFromArranged });
      event.dataTransfer.effectAllowed = "move";
    };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const target = event.currentTarget as HTMLElement;
    target.classList.add("drag-over");
  };

  const handleDragLeave = (event: React.DragEvent) => {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove("drag-over");
  };

  const handleDropOnArranged = (event: React.DragEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");

    if (!draggedWord || disabled || reviewMode) return;

    if (!draggedWord.fromArranged) {
      const newAvailable = availableWords.filter(
        (w) => w.word !== draggedWord.word
      );
      const newArranged = [...arrangedWords, draggedWord];

      setAvailableWords(newAvailable);
      setArrangedWords(newArranged);
      syncAnswerToParent(newArranged);
    }

    setDraggedWord(null);
  };

  const handleDropOnAvailable = (event: React.DragEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");

    if (!draggedWord || disabled || reviewMode) return;

    if (draggedWord.fromArranged) {
      const newArranged = arrangedWords.filter(
        (w) => w.word !== draggedWord.word
      );
      const newAvailable = shuffleArray([...availableWords, draggedWord]);

      setArrangedWords(newArranged);
      setAvailableWords(newAvailable);
      syncAnswerToParent(newArranged);
    }

    setDraggedWord(null);
  };

  // ============================================================
  // CLICK HANDLERS
  // ============================================================
  const handleWordClick = (word: WordOption, isFromArranged: boolean) => {
    if (disabled || reviewMode) return;

    if (isFromArranged) {
      const newArranged = arrangedWords.filter((w) => w.word !== word.word);
      const newAvailable = shuffleArray([...availableWords, word]);

      setArrangedWords(newArranged);
      setAvailableWords(newAvailable);
      syncAnswerToParent(newArranged);
    } else {
      const newAvailable = availableWords.filter((w) => w.word !== word.word);
      const newArranged = [...arrangedWords, word];

      setAvailableWords(newAvailable);
      setArrangedWords(newArranged);
      syncAnswerToParent(newArranged);
    }
  };

  // ============================================================
  // RESET HANDLER
  // ============================================================
  const handleReset = () => {
    const shuffled = shuffleArray([...question.wordOptions]);
    setAvailableWords(shuffled);
    setArrangedWords([]);
    setDraggedWord(null);
    startTimeRef.current = Date.now();
    syncAnswerToParent([]);
  };

  // ============================================================
  // RENDER
  // ============================================================
  const userSentence = arrangedWords.map((w) => w.word).join(" ");

  return (
    <QuestionContainer>
      {/* Question Header */}
      <QuestionHeader>
        <Typography variant="h6" gutterBottom>
          🔧 {question.question}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {question.skill} • {question.points} points
        </Typography>
        <Chip
          label={`${question.module} - Week ${question.week}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </QuestionHeader>

      {/* Instructions */}
      <InstructionsContainer severity="info" icon={<BuildIcon />}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          Sentence Building Instructions:
        </Typography>
        <Typography variant="body2">
          • Drag and drop or click words to arrange them in order
          <br />
          • Click arranged words to move them back
          <br />• Use all words to build the correct sentence
        </Typography>
      </InstructionsContainer>

      {/* Workspace */}
      <WorkspaceContainer elevation={2}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <BuildIcon color="primary" />
          Your Sentence ({arrangedWords.length}/{question.wordOptions.length}{" "}
          words)
          {isComplete && (
            <Chip
              label="Complete"
              size="small"
              color="success"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        <DroppableArea
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDropOnArranged}
        >
          {arrangedWords.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Drag words here to build your sentence...
            </Typography>
          ) : (
            arrangedWords.map((word, index) => (
              <PlacedWordChip
                key={`arranged-${word.word}-${index}`}
                label={word.word}
                onDelete={
                  !disabled && !reviewMode
                    ? () => handleWordClick(word, true)
                    : undefined
                }
                deleteIcon={<CancelIcon />}
                draggable={!disabled && !reviewMode}
                onDragStart={handleDragStart(word, true)}
                onClick={() =>
                  !disabled && !reviewMode && handleWordClick(word, true)
                }
                disabled={disabled || reviewMode}
              />
            ))
          )}
        </DroppableArea>

        {/* Current sentence preview */}
        {arrangedWords.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "white",
              borderRadius: 1,
              border: "1px solid #ddd",
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current sentence:
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontFamily: "serif", fontStyle: "italic" }}
            >
              "{userSentence}"
            </Typography>
          </Box>
        )}
      </WorkspaceContainer>

      {/* Word Bank */}
      <WordBankContainer elevation={1}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <DragIndicatorIcon color="primary" />
          Word Bank ({availableWords.length} words remaining)
        </Typography>

        <DroppableArea
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDropOnAvailable}
          sx={{
            minHeight: "60px",
            border: "1px solid #ddd",
            borderStyle: "solid",
          }}
        >
          {availableWords.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              All words have been used!
            </Typography>
          ) : (
            availableWords.map((word, index) => (
              <WordChip
                key={`available-${word.word}-${index}`}
                label={word.word}
                color="default"
                variant="outlined"
                draggable={!disabled && !reviewMode}
                onDragStart={handleDragStart(word, false)}
                onClick={() =>
                  !disabled && !reviewMode && handleWordClick(word, false)
                }
                disabled={disabled || reviewMode}
              />
            ))
          )}
        </DroppableArea>
      </WordBankContainer>

      {/* Reset Button */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          startIcon={<RestartAltIcon />}
          disabled={
            (availableWords.length === question.wordOptions.length &&
              arrangedWords.length === 0) ||
            disabled ||
            reviewMode
          }
        >
          Reset Words
        </Button>
      </Box>

      {/* Results Section */}
      {showResults && reviewMode && (
        <ResultContainer
          sx={{
            borderColor: isCorrect
              ? theme.palette.success.main
              : theme.palette.error.main,
            backgroundColor: isCorrect
              ? theme.palette.success.light + "20"
              : theme.palette.error.light + "20",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            {isCorrect ? (
              <CheckCircleIcon
                sx={{ color: "success.main", fontSize: 48, mr: 1 }}
              />
            ) : (
              <CancelIcon sx={{ color: "error.main", fontSize: 48, mr: 1 }} />
            )}
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {isCorrect ? "Perfect Sentence!" : "Not Quite Right"}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {isCorrect
              ? "Excellent! You built the sentence correctly."
              : `Your answer: "${userSentence}"`}
          </Typography>

          {!isCorrect && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Correct order:
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontFamily: "serif", fontStyle: "italic", mt: 1 }}
              >
                "{correctSentence}"
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary">
            Points earned: {isCorrect ? question.points : 0} / {question.points}
          </Typography>
        </ResultContainer>
      )}
    </QuestionContainer>
  );
};

export default SentenceBuildingComponent;
